import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface EditNameDialogProps {
  currentName: string | null;
  userId: string;
  onNameUpdated: (newName: string) => void;
}

export function EditNameDialog({ currentName, userId, onNameUpdated }: EditNameDialogProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(currentName || '');
  const [isLoading, setIsLoading] = useState(false);

  const handleSave = async () => {
    const trimmedName = name.trim();
    
    if (trimmedName.length < 2) {
      toast.error('O nome deve ter pelo menos 2 caracteres');
      return;
    }
    
    if (trimmedName.length > 50) {
      toast.error('O nome deve ter no máximo 50 caracteres');
      return;
    }

    setIsLoading(true);
    
    const { error } = await supabase
      .from('profiles')
      .update({ display_name: trimmedName })
      .eq('user_id', userId);

    setIsLoading(false);

    if (error) {
      console.error('Error updating name:', error);
      toast.error('Erro ao atualizar nome');
      return;
    }

    toast.success('Nome atualizado com sucesso');
    onNameUpdated(trimmedName);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
          <Pencil className="h-4 w-4 text-muted-foreground" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Editar nome</DialogTitle>
          <DialogDescription>
            Digite seu nome de exibição. Este nome será visível no seu perfil.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">Nome</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Seu nome"
              maxLength={50}
            />
            <p className="text-xs text-muted-foreground">
              {name.length}/50 caracteres
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
